import { useState } from 'react'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { MeshGradient } from '../components/AnimatedBackground'
import {
  ArrowLeft,
  Book,
  Database,
  Play,
  Settings,
  Shield,
  Zap,
  Key,
  HardDrive,
  Search,
  ChevronRight,
  Crown,
  Clock
} from 'lucide-react'

type DocSection =
  | 'getting-started'
  | 'connections'
  | 'querying'
  | 'data-browser'
  | 'change-review'
  | 'performance'
  | 'governance'
  | 'backup'
  | 'secrets'
  | 'settings'

interface DocNav {
  id: DocSection
  title: string
  icon: React.ReactNode
  isPro?: boolean
}

const DOC_NAV: DocNav[] = [
  { id: 'getting-started', title: 'Getting Started', icon: <Play className="w-4 h-4" /> },
  { id: 'connections', title: 'Database Connections', icon: <Database className="w-4 h-4" /> },
  { id: 'querying', title: 'Query Editor', icon: <Search className="w-4 h-4" /> },
  { id: 'data-browser', title: 'Data Browser', icon: <Book className="w-4 h-4" /> },
  { id: 'change-review', title: 'Change Review', icon: <Shield className="w-4 h-4" /> },
  { id: 'performance', title: 'Performance Advisor', icon: <Zap className="w-4 h-4" />, isPro: true },
  { id: 'governance', title: 'Governance', icon: <Shield className="w-4 h-4" />, isPro: true },
  { id: 'backup', title: 'Backup & Restore', icon: <HardDrive className="w-4 h-4" />, isPro: true },
  { id: 'secrets', title: 'Secrets Manager', icon: <Key className="w-4 h-4" />, isPro: true },
  { id: 'settings', title: 'Settings', icon: <Settings className="w-4 h-4" /> }
]

export function Documentation() {
  const [activeSection, setActiveSection] = useState<DocSection>('getting-started')

  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="legal" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>

          <div className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Documentation</h1>
            <p className="text-zinc-400">Learn how to use Nexatice effectively</p>
          </div>

          <div className="docs-layout">
            <nav className="docs-nav">
              {DOC_NAV.map((item) => (
                <button
                  key={item.id}
                  className={`docs-nav-item ${activeSection === item.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(item.id)}
                >
                  {item.icon}
                  <span className="flex-1 text-left">{item.title}</span>
                  {item.isPro && (
                    <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                      PRO
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4" />
                </button>
              ))}
            </nav>

            <div className="docs-content">
              {activeSection === 'getting-started' && <GettingStartedDoc />}
              {activeSection === 'connections' && <ConnectionsDoc />}
              {activeSection === 'querying' && <QueryingDoc />}
              {activeSection === 'data-browser' && <DataBrowserDoc />}
              {activeSection === 'change-review' && <ChangeReviewDoc />}
              {activeSection === 'performance' && <PerformanceDoc />}
              {activeSection === 'governance' && <GovernanceDoc />}
              {activeSection === 'backup' && <BackupDoc />}
              {activeSection === 'secrets' && <SecretsDoc />}
              {activeSection === 'settings' && <SettingsDoc />}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

function GettingStartedDoc() {
  return (
    <article className="doc-article">
      <h2>Getting Started with Nexatice</h2>
      <p className="doc-lead">
        Nexatice is a powerful, modern database management tool that helps you work with multiple database
        engines from a single, unified interface.
      </p>

      <h3>Installation</h3>
      <p>
        Download Nexatice from the <a href="/#download">download page</a> or
        your dashboard. Currently available for Windows, with macOS and Linux support coming later.
      </p>
      <ul>
        <li><strong>Windows:</strong> Run the installer (.exe) and follow the prompts</li>
      </ul>
      <p className="text-sm text-zinc-500 mt-2">
        <em>macOS and Linux versions are in development and will be available soon.</em>
      </p>

      <h3>First Launch</h3>
      <p>When you first open Nexatice, you'll be greeted with an empty workspace. Here's what to do next:</p>
      <ol>
        <li>Click the <strong>+ New Connection</strong> button in the sidebar</li>
        <li>Select your database engine (MySQL, PostgreSQL, MongoDB, SQLite, Redis, Oracle, or NeonDB)</li>
        <li>Enter your connection details</li>
        <li>Click <strong>Test Connection</strong> to verify everything works</li>
        <li>Click <strong>Save</strong> to add the connection</li>
      </ol>

      <h3>Interface Overview</h3>
      <p>The Nexatice interface consists of several key areas:</p>
      <ul>
        <li><strong>Sidebar:</strong> Contains your connections, navigation, and tools</li>
        <li><strong>Main Area:</strong> Displays your active view (Query Editor, Data Browser, etc.)</li>
        <li><strong>Tab Bar:</strong> Manage multiple open queries and views</li>
        <li><strong>Status Bar:</strong> Shows connection status and notifications</li>
      </ul>

      <div className="doc-tip">
        <strong>Pro Tip:</strong> Use keyboard shortcuts to work faster. Press <kbd>Ctrl</kbd> + <kbd>,</kbd> to
        open Settings and view all available shortcuts.
      </div>
    </article>
  )
}

function ConnectionsDoc() {
  return (
    <article className="doc-article">
      <h2>Database Connections</h2>
      <p className="doc-lead">
        Nexatice supports multiple database engines with secure connection options including SSH tunnels
        and SSL/TLS encryption.
      </p>

      <h3>Supported Databases</h3>
      <ul>
        <li><strong>MySQL</strong> - Version 5.7 and above</li>
        <li><strong>PostgreSQL</strong> - Version 10 and above</li>
        <li><strong>MongoDB</strong> - Version 4.0 and above</li>
        <li><strong>SQLite</strong> - Version 3</li>
        <li><strong>Redis</strong> - Version 5.0 and above</li>
        <li><strong>Oracle</strong> - Version 12c and above (requires Oracle Instant Client)</li>
        <li><strong>NeonDB</strong> - Serverless PostgreSQL (all versions)</li>
      </ul>

      <h3>NeonDB Setup</h3>
      <p>
        NeonDB is a serverless PostgreSQL database that requires SSL connections. To connect:
      </p>
      <ol>
        <li>Log in to your <a href="https://neon.tech" target="_blank" rel="noopener">Neon Console</a></li>
        <li>Go to your project's <strong>Connection Details</strong></li>
        <li>Copy the connection string (it includes all credentials)</li>
        <li>In Nexatice, select <strong>NeonDB</strong> and paste the connection string</li>
      </ol>
      <p>
        SSL is automatically enabled for NeonDB connections. You can also enter host, database, 
        username, and password separately if preferred.
      </p>

      <h3>Oracle Setup</h3>
      <p>
        Oracle connections require the <strong>Oracle Instant Client</strong> to be installed on your system.
        This is a free download from Oracle's website.
      </p>
      <ol>
        <li>Download Oracle Instant Client (Basic or Basic Light) from <a href="https://www.oracle.com/database/technologies/instant-client/downloads.html" target="_blank" rel="noopener">oracle.com</a></li>
        <li>Extract the files to a folder (e.g., <code>C:\oracle\instantclient_21_x</code>)</li>
        <li>In Nexatice, go to <strong>Settings → Database → Oracle Client Path</strong></li>
        <li>Set the path to your Instant Client folder</li>
        <li>Restart Nexatice for changes to take effect</li>
      </ol>
      <p>
        For Oracle Cloud (Autonomous Database), you'll also need a <strong>Wallet</strong> for secure connections.
        Download your wallet from the Oracle Cloud Console and specify the wallet path in the connection settings.
      </p>

      <div className="doc-tip">
        <strong>Tip:</strong> If you see "DPI-1047" errors, ensure the Instant Client path is set correctly in Settings.
        If you see "NJS-090" errors, restart Nexatice after changing the client path.
      </div>

      <h3>Creating a Connection</h3>
      <ol>
        <li>Click <strong>+ New Connection</strong> in the sidebar</li>
        <li>Choose your database engine</li>
        <li>Fill in the connection details:
          <ul>
            <li><strong>Name:</strong> A friendly name for this connection</li>
            <li><strong>Host:</strong> The server address (e.g., localhost or IP)</li>
            <li><strong>Port:</strong> The database port (defaults are provided)</li>
            <li><strong>Database:</strong> The database/schema name</li>
            <li><strong>Username/Password:</strong> Your credentials</li>
          </ul>
        </li>
        <li>Optionally configure SSH tunnel or SSL/TLS</li>
        <li>Test and save the connection</li>
      </ol>

      <h3>SSH Tunnel</h3>
      <p>
        For databases behind firewalls, use SSH tunneling to create a secure connection through an
        intermediate server:
      </p>
      <ol>
        <li>Enable "Use SSH Tunnel" in the connection form</li>
        <li>Enter your SSH server details (host, port, username)</li>
        <li>Provide either a password or private key file</li>
      </ol>

      <h3>SSL/TLS Encryption</h3>
      <p>
        Enable SSL/TLS to encrypt the connection between Nexatice and your database server. This is
        recommended for production databases and required by many cloud providers.
      </p>

      <div className="doc-warning">
        <strong>Security Note:</strong> Your credentials are encrypted and stored locally on your device.
        Nexatice never transmits your credentials to external servers.
      </div>
    </article>
  )
}

function QueryingDoc() {
  return (
    <article className="doc-article">
      <h2>Query Editor</h2>
      <p className="doc-lead">
        The Query Editor is a powerful Monaco-based code editor with IntelliSense, syntax highlighting,
        and auto-completion for SQL and other query languages.
      </p>

      <h3>Writing Queries</h3>
      <p>
        The editor provides intelligent assistance as you type:
      </p>
      <ul>
        <li><strong>Auto-completion:</strong> Table names, column names, and SQL keywords</li>
        <li><strong>Syntax highlighting:</strong> Color-coded SQL for readability</li>
        <li><strong>Error detection:</strong> Highlights syntax errors before execution</li>
        <li><strong>Multiple cursors:</strong> Edit multiple lines simultaneously</li>
      </ul>

      <h3>Executing Queries</h3>
      <p>Execute your queries using any of these methods:</p>
      <ul>
        <li><strong>Run All:</strong> Press <kbd>Ctrl</kbd> + <kbd>Enter</kbd> or click the <strong>Run</strong> button</li>
        <li><strong>Run Selected:</strong> Select text and press <kbd>Ctrl</kbd> + <kbd>Shift</kbd> + <kbd>Enter</kbd> to run only the selection</li>
        <li><strong>Run From Cursor:</strong> Press <kbd>Ctrl</kbd> + <kbd>Alt</kbd> + <kbd>Enter</kbd> to run everything from cursor to end</li>
      </ul>
      <p>Use the dropdown arrow next to the Run button to access these options.</p>

      <h3>Query Results</h3>
      <p>Results are displayed in a grid below the editor with features like:</p>
      <ul>
        <li>Column sorting and filtering</li>
        <li>Cell value copying</li>
        <li>Export to CSV, JSON, or SQL</li>
        <li>Result pagination for large datasets</li>
      </ul>

      <h3>Query History</h3>
      <p>
        All executed queries are saved to your history. Access it from the sidebar to re-run or
        modify previous queries. Click on any history item to see the full query, execution time,
        and quickly insert it back into the editor.
      </p>

      <h3>Saved Snippets</h3>
      <p>
        Save frequently used queries as snippets for quick access. Organize them into categories
        for better management. Each snippet can be associated with a specific database engine.
      </p>

      <h3>Detachable Results Panel</h3>
      <p>
        Pop out the query results into a separate window for multi-monitor setups. The detached
        window stays synchronized with the main window, showing results from all your queries.
      </p>
    </article>
  )
}

function DataBrowserDoc() {
  return (
    <article className="doc-article">
      <h2>Data Browser</h2>
      <p className="doc-lead">
        Browse and edit your data directly in a spreadsheet-like interface with inline editing,
        filtering, and sorting capabilities.
      </p>

      <h3>Browsing Tables (SQL Databases)</h3>
      <p>
        For MySQL, PostgreSQL, SQLite, Oracle, and NeonDB, select a table from the sidebar to view its contents. 
        The Data Browser displays data in a grid format with:
      </p>
      <ul>
        <li>Column headers with data types</li>
        <li>Row numbers for reference</li>
        <li>Pagination controls for large tables</li>
        <li>Quick search and filtering</li>
      </ul>

      <h3>Redis Key Browser</h3>
      <p>
        For Redis connections, the Data Browser shows a specialized Key Browser with:
      </p>
      <ul>
        <li><strong>List View:</strong> Flat list of all keys with type badges</li>
        <li><strong>Tree View:</strong> Hierarchical view based on key prefixes (using : as separator)</li>
        <li><strong>Type Support:</strong> String, Hash, List, Set, Sorted Set</li>
        <li><strong>Inline Editing:</strong> Edit values directly in the browser</li>
        <li><strong>Add/Delete:</strong> Add fields to hashes, items to lists/sets, or delete keys</li>
      </ul>

      <h3>Editing Data</h3>
      <p>Double-click any cell to edit its value. Changes are tracked and highlighted:</p>
      <ul>
        <li><strong>Yellow:</strong> Modified cells (pending save)</li>
        <li><strong>Orange:</strong> Changes staged for review</li>
        <li><strong>Green:</strong> New rows/fields</li>
        <li><strong>Red:</strong> Items marked for deletion</li>
      </ul>

      <h3>Saving Changes</h3>
      <p>
        By default, changes are staged for review before being applied to the database. This
        ensures you can review all modifications before committing them.
      </p>
      <p>
        If you prefer immediate saves, enable <strong>Instant Save Mode</strong> in Settings.
        Note that this bypasses the Change Review system.
      </p>

      <h3>Table Designer</h3>
      <p>
        Create and modify tables visually using the Table Designer. Access it via the "+" button
        in the Tables sidebar or right-click a table and select "Edit Table Structure".
      </p>
      <ul>
        <li><strong>Columns:</strong> Add, edit, or remove columns with full type support</li>
        <li><strong>Primary Keys:</strong> Set single or composite primary keys</li>
        <li><strong>Indexes:</strong> Create indexes for better query performance</li>
        <li><strong>Foreign Keys:</strong> Define relationships between tables</li>
        <li><strong>DDL Preview:</strong> See the generated SQL before executing</li>
      </ul>

      <h3>Generate Scripts</h3>
      <p>
        Right-click any table to generate SQL scripts:
      </p>
      <ul>
        <li><strong>CREATE TABLE:</strong> Full DDL to recreate the table</li>
        <li><strong>DROP TABLE:</strong> Statement to delete the table</li>
        <li><strong>TRUNCATE:</strong> Clear all data from the table</li>
        <li><strong>INSERT Statements:</strong> Generate INSERT for selected rows</li>
      </ul>

      <div className="doc-tip">
        <strong>Pro Tip:</strong> Use <kbd>Ctrl</kbd> + <kbd>Z</kbd> to undo changes and
        <kbd>Ctrl</kbd> + <kbd>Y</kbd> to redo them before saving.
      </div>
    </article>
  )
}

function ChangeReviewDoc() {
  return (
    <article className="doc-article">
      <h2>Change Review</h2>
      <p className="doc-lead">
        The Change Review system helps prevent accidental data modifications by staging all changes
        for review before they're applied to your database.
      </p>

      <h3>How It Works</h3>
      <ol>
        <li>Make changes in the Data Browser or Query Editor</li>
        <li>Changes are automatically staged (not applied yet)</li>
        <li>Open the Change Review panel to see all pending changes</li>
        <li>Review the SQL that will be executed</li>
        <li>Approve or reject individual changes</li>
        <li>Execute approved changes</li>
      </ol>

      <h3>Reviewing Changes</h3>
      <p>The Change Review panel shows:</p>
      <ul>
        <li>Type of change (INSERT, UPDATE, DELETE, DDL)</li>
        <li>Affected table and rows</li>
        <li>The actual SQL to be executed</li>
        <li><strong>Before/After diff:</strong> See exactly what values changed with + and - indicators</li>
        <li>Filter by connection when working with multiple databases</li>
      </ul>
      
      <h3>View Modes</h3>
      <p>Switch between different views to review your changes:</p>
      <ul>
        <li><strong>SQL View:</strong> See the raw SQL statements</li>
        <li><strong>Tree View:</strong> Hierarchical view of changes with diff highlighting</li>
        <li><strong>Visual View:</strong> Side-by-side before/after comparison</li>
      </ul>

      <h3>Instant Save Mode</h3>
      <p>
        If you prefer to skip the review process, enable Instant Save Mode in Settings. Changes
        will be applied immediately when you click Save.
      </p>

      <div className="doc-warning">
        <strong>Warning:</strong> Instant Save Mode bypasses all safety checks. Use with caution
        on production databases.
      </div>
    </article>
  )
}

function ProFeatureBanner() {
  return (
    <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
          <Crown className="w-5 h-5 text-amber-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-white">Pro Feature</span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-800 text-xs text-zinc-400">
              <Clock className="w-3 h-3" />
              Work in Progress
            </span>
          </div>
          <p className="text-sm text-zinc-400 mt-0.5">
            This feature is included in the app but is still under development and may not work as expected.
            Full functionality coming with Nexatice Pro (early 2026). <a href="/#pricing" className="text-amber-400 hover:text-amber-300">Learn more</a>
          </p>
        </div>
      </div>
    </div>
  )
}

function PerformanceDoc() {
  return (
    <article className="doc-article">
      <h2>Performance Advisor</h2>
      <ProFeatureBanner />
      <p className="doc-lead">
        The Performance Advisor analyzes your queries and database structure to provide optimization
        recommendations.
      </p>

      <h3>Query Analysis</h3>
      <p>Run any query through the Performance Advisor to get:</p>
      <ul>
        <li>Execution plan visualization</li>
        <li>Index usage analysis</li>
        <li>Suggested optimizations</li>
        <li>Estimated performance impact</li>
      </ul>

      <h3>Index Recommendations</h3>
      <p>
        The advisor scans your query patterns and suggests indexes that could improve performance.
        Each recommendation includes:
      </p>
      <ul>
        <li>The suggested index definition</li>
        <li>Expected performance improvement</li>
        <li>Disk space requirements</li>
        <li>One-click index creation</li>
      </ul>

      <h3>Slow Query Log</h3>
      <p>
        Track queries that take longer than expected. Set thresholds and get notified when
        queries exceed them.
      </p>
    </article>
  )
}

function GovernanceDoc() {
  return (
    <article className="doc-article">
      <h2>Governance & Approvals</h2>
      <ProFeatureBanner />
      <p className="doc-lead">
        Set up approval workflows and access controls to manage database changes in team environments.
      </p>

      <h3>Approval Workflows</h3>
      <p>Configure rules that require approval before certain operations:</p>
      <ul>
        <li>DDL changes (CREATE, ALTER, DROP)</li>
        <li>Bulk data modifications</li>
        <li>Changes to specific tables</li>
        <li>Production database operations</li>
      </ul>

      <h3>Access Controls</h3>
      <p>Define who can perform what actions:</p>
      <ul>
        <li>Read-only access for certain connections</li>
        <li>Restricted DDL permissions</li>
        <li>Table-level access controls</li>
      </ul>

      <h3>Audit Trail</h3>
      <p>
        Every action is logged with timestamps, user information, and the exact changes made.
        Export audit logs for compliance reporting.
      </p>
    </article>
  )
}

function BackupDoc() {
  return (
    <article className="doc-article">
      <h2>Backup & Restore</h2>
      <ProFeatureBanner />
      <p className="doc-lead">
        Create and manage database backups with scheduling, compression, and easy restoration.
      </p>

      <h3>Creating Backups</h3>
      <ol>
        <li>Select the connection to back up</li>
        <li>Choose backup options (full, schema-only, data-only)</li>
        <li>Set compression level</li>
        <li>Start the backup</li>
      </ol>

      <h3>Scheduled Backups</h3>
      <p>
        Set up automated backup schedules to run daily, weekly, or at custom intervals.
        Configure retention policies to automatically clean up old backups.
      </p>

      <h3>Restoring Data</h3>
      <p>
        Restore from any backup with options to:
      </p>
      <ul>
        <li>Restore to the same database</li>
        <li>Restore to a different database</li>
        <li>Preview changes before restoring</li>
        <li>Selective table restoration</li>
      </ul>
    </article>
  )
}

function SecretsDoc() {
  return (
    <article className="doc-article">
      <h2>Secrets Manager</h2>
      <ProFeatureBanner />
      <p className="doc-lead">
        Securely store and manage sensitive credentials, API keys, and connection strings.
      </p>

      <h3>Storing Secrets</h3>
      <p>
        Add secrets to encrypted vaults that are stored locally on your device. Each secret includes:
      </p>
      <ul>
        <li>A unique name/identifier</li>
        <li>The secret value (encrypted at rest)</li>
        <li>Optional metadata and tags</li>
        <li>Access audit trail</li>
      </ul>

      <h3>Using Secrets</h3>
      <p>
        Reference secrets in your connection configurations using the secret selector. Secrets are
        decrypted only when needed and never stored in plain text.
      </p>

      <h3>Security</h3>
      <ul>
        <li>AES-256 encryption at rest</li>
        <li>Secrets never leave your device</li>
        <li>Access logging for compliance</li>
        <li>Optional master password protection</li>
      </ul>
    </article>
  )
}

function SettingsDoc() {
  return (
    <article className="doc-article">
      <h2>Settings</h2>
      <p className="doc-lead">
        Customize Nexatice to match your workflow with extensive configuration options.
      </p>

      <h3>Appearance</h3>
      <ul>
        <li><strong>Theme:</strong> Choose between Dark, Light, or System</li>
        <li><strong>Accent Color:</strong> Select your preferred highlight color</li>
        <li><strong>Font Size:</strong> Adjust the editor font size</li>
        <li><strong>Sidebar Width:</strong> Customize the sidebar size</li>
      </ul>

      <h3>Editor</h3>
      <ul>
        <li><strong>Word Wrap:</strong> How to handle long lines</li>
        <li><strong>Tab Size:</strong> Indentation width</li>
        <li><strong>Auto-complete:</strong> Enable/disable suggestions</li>
        <li><strong>Line Numbers:</strong> Show/hide line numbers</li>
        <li><strong>Minimap:</strong> Show/hide code minimap</li>
      </ul>

      <h3>Database</h3>
      <ul>
        <li><strong>Max Rows:</strong> Limit rows per query</li>
        <li><strong>Query Timeout:</strong> Maximum execution time</li>
        <li><strong>Confirm Destructive:</strong> Warn on DELETE/DROP/TRUNCATE</li>
        <li><strong>Instant Save:</strong> Bypass Change Review</li>
        <li><strong>Notification Duration:</strong> How long notifications stay visible</li>
      </ul>

      <h3>Updates</h3>
      <ul>
        <li><strong>Automatic Update Check:</strong> Check for updates when the app starts</li>
        <li><strong>Update Notifications:</strong> Show notifications when updates are available</li>
      </ul>
      <p>
        When an update is available, you'll be notified and can download it directly in the app.
        The update will be installed when you restart.
      </p>

      <h3>Keyboard Shortcuts</h3>
      <p>View and reference all available keyboard shortcuts in the Shortcuts tab.</p>
      
      <h3>License</h3>
      <p>
        Activate your license key to unlock Pro features. Your license is bound to your machine
        and validated periodically. The app works offline for up to 14 days between validations.
      </p>
    </article>
  )
}

